import { createContext, useContext, useEffect } from 'react'

import type { TabId } from './components/TabBar'
import { showBackButton } from './telegram'

/**
 * Вложенный экран поверх вкладки. Размеченное объединение, а не строка с
 * параметрами: App обязан разобрать все варианты (switch с проверкой never),
 * поэтому новый экран невозможно завести и забыть отрисовать.
 */
export type Screen =
  | { kind: 'chapter'; id: string; title: string }
  | { kind: 'rooms' }
  | { kind: 'room'; system: 'bazi' | 'ziwei' | 'tzolkin' }
  | { kind: 'cycle'; period: 'wave' | 'month' | 'year' | 'decade' }
  | { kind: 'lifescale' }
  | { kind: 'symbols' }
  | { kind: 'symbol'; id: string }
  | { kind: 'journal' }
  | { kind: 'crossings' }
  | { kind: 'access' }

export interface NavState {
  tab: TabId
  screen: Screen | null
}

/**
 * Цель перехода. `screen: null` — вернуться на саму вкладку; смена вкладки без
 * указания экрана всегда сбрасывает вложенный экран.
 */
export interface NavTarget {
  tab?: TabId
  screen?: Screen | null
}

export interface Nav extends NavState {
  navigate: (target: NavTarget) => void
  back: () => void
}

const FALLBACK: Nav = {
  tab: 'today',
  screen: null,
  navigate: () => undefined,
  back: () => undefined,
}

export const NavContext = createContext<Nav>(FALLBACK)

export function useNav(): Nav {
  return useContext(NavContext)
}

/**
 * Системная кнопка «назад» Telegram. Один хук на всё приложение: экраны её не
 * трогают, иначе два разных useEffect начинают перетягивать одну и ту же
 * кнопку и стрелка остаётся висеть на главной. `null` — кнопки нет.
 */
export function useBackButton(handler: (() => void) | null): void {
  useEffect(() => {
    if (!handler) return undefined
    return showBackButton(handler)
  }, [handler])
}
